import React from 'react';
import { STORE_ADDRESS, CURRENCY_SYMBOLS } from '../config/receipts.config';

/**
 * Render a single field's display value with its prefix/suffix.
 * Currency values are formatted with thousands separators + 2 decimals,
 * and use the receipt's selected currency symbol as prefix.
 */
function renderValue(field, raw, currency, values = {}) {
  const empty = raw === undefined || raw === null || raw === '';
  // `always` fields (e.g. custody saldos) render even when empty → show 0.00.
  if (empty && !(field.type === 'fixedCurrency' && field.always)) return '';

  let val = raw;
  let prefix = field.prefix ? `${field.prefix} ` : '';

  if (field.type === 'currencyPick') {
    const n = parseFloat(raw);
    if (Number.isFinite(n)) {
      val = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    }
    // Currency chosen per-field, stored in values[`${key}_cur`] (default USD).
    const cur = values[`${field.key}_cur`] || 'USD';
    const sym = CURRENCY_SYMBOLS[cur] || '';
    prefix = sym ? `${sym} ` : '';
  } else if (field.type === 'currency') {
    const n = parseFloat(raw);
    if (Number.isFinite(n)) {
      val = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    }
    // Currency symbol from the selected currency overrides the field's static prefix.
    const sym = CURRENCY_SYMBOLS[currency] || field.prefix || '';
    prefix = sym ? `${sym} ` : '';
  } else if (field.type === 'fixedCurrency') {
    const n = parseFloat(raw);
    if (Number.isFinite(n)) {
      val = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    } else if (field.always) {
      val = '0.00';
    }
    // Each fixed-currency field carries its own symbol.
    const sym = CURRENCY_SYMBOLS[field.fixedCurrency] || '';
    prefix = sym ? `${sym} ` : '';
  } else if (field.type === 'weight') {
    const n = parseFloat(raw);
    if (Number.isFinite(n)) val = `${n}`;
  }

  const suffix = field.suffix ? `${field.suffix}` : '';
  return `${prefix}${val}${suffix}`;
}

// Format a raw number as money (2 decimals, thousands sep). No symbol.
function money(raw) {
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

// Format grams (trim trailing). No symbol.
function grams(raw) {
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return '';
  return `${n}`;
}

/**
 * Custom body for the "Compra de Ouro" receipt:
 *   {peso} gr  X  {preco}
 *   TOTAL:  {sym} {total}
 *   PAGO: + {sym} {pago}
 */
function CompraOuroBody({ values, currency, t }) {
  const sym = CURRENCY_SYMBOLS[currency] || '';
  const peso = grams(values.peso);
  const preco = money(values.preco);
  const total = money(values.total);

  // Paid lines: one per currency actually used (non-zero amount).
  const pagos = [
    { cur: 'USD', val: values.pago_usd },
    { cur: 'EUR', val: values.pago_eur },
    { cur: 'SRD', val: values.pago_srd },
  ].filter((p) => {
    const n = parseFloat(p.val);
    return Number.isFinite(n) && n !== 0;
  });

  return (
    <div className="receipt-compra">
      {(peso || preco) && (
        <div className="receipt-calc">
          {peso ? `${peso} gr` : ''}{(peso && preco) ? '   X   ' : ''}{preco}
        </div>
      )}
      <div className="receipt-total-line">
        {t('rcpt_f_total')}:&nbsp;&nbsp;{sym} {total}
      </div>
      {pagos.length > 0 && (
        <div className="receipt-pago-block">
          <div className="receipt-pago-label">{t('rcpt_f_pago')}:</div>
          {pagos.map((p) => (
            <div className="receipt-pago-line" key={p.cur}>
              − {CURRENCY_SYMBOLS[p.cur]} {money(p.val)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Custom body for a register (Kassa) transaction receipt: date, description,
 * and the amounts. Cambio shows the received amount + the amounts given back.
 */
function KassaBody({ values, t }) {
  const isCambio = values.kind === 'cambio';
  const isRec = values.kind === 'recebido';
  const sign = isRec ? '+' : '−';
  const amounts = [['USD', 'amount_usd'], ['SRD', 'amount_srd'], ['EUR', 'amount_eur']]
    .map(([cur, k]) => ({ cur, val: values[k] }))
    .filter((p) => { const n = parseFloat(p.val); return Number.isFinite(n) && n !== 0; });

  return (
    <>
      <table className="receipt-table">
        <tbody>
          {values.datetime && (
            <tr><td className="receipt-label">{t('date')}</td><td className="receipt-val">{values.datetime}</td></tr>
          )}
          {values.description && (
            <tr><td className="receipt-label">{t('description')}</td><td className="receipt-val">{values.description}</td></tr>
          )}
        </tbody>
      </table>
      <div className="receipt-compra">
        {isCambio ? (
          <>
            <div className="receipt-total-line">
              {t('kassa_received')}:&nbsp;&nbsp;{CURRENCY_SYMBOLS[(values.recv_currency || '').toUpperCase()] || ''} {money(values.recv_amount)}
            </div>
            {amounts.length > 0 && (
              <div className="receipt-pago-block">
                <div className="receipt-pago-label">{t('rcpt_kassa_given')}:</div>
                {amounts.map((p) => (
                  <div className="receipt-pago-line" key={p.cur}>− {CURRENCY_SYMBOLS[p.cur]} {money(p.val)}</div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="receipt-pago-block">
            {amounts.map((p) => (
              <div className="receipt-pago-line" key={p.cur}>{sign} {CURRENCY_SYMBOLS[p.cur]} {money(p.val)}</div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * 80mm receipt preview. This exact markup is what gets printed
 * (see receipt-print.css). Mirrors the client's Excel layout:
 * logo + store header, bold underlined title, bordered field table,
 * signature line, italic footer + timestamp.
 *
 * @param {object} props
 * @param {object} props.type     receipt type definition (from receipts.config)
 * @param {object} props.values   { [fieldKey]: value }
 * @param {function} props.t      i18n
 */
export default function ReceiptPreview({ type, values = {}, currency = 'USD', t, domId = 'receipt-print-area' }) {
  if (!type) return null;

  // Only render rows that have a value, plus required rows (so blanks are visible).
  // Fixed-currency amount rows are dropped when empty OR zero, so a receipt shows
  // only the currencies actually used.
  const rows = type.fields.filter((f) => {
    const v = values[f.key];
    if (f.type === 'fixedCurrency') {
      if (f.always) return true;           // always render (e.g. custody saldos)
      const n = parseFloat(v);
      return Number.isFinite(n) && n !== 0;
    }
    return f.required || (v !== undefined && v !== null && v !== '');
  });

  const now = new Date();
  const timestamp = `${now.toLocaleDateString()} ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

  return (
    <div className="receipt-paper" id={domId}>
      <div className="receipt-header">
        <img
          className="receipt-logo"
          src={process.env.PUBLIC_URL + '/assets/logo_receipt.PNG'}
          alt="GL"
        />
        <div className="receipt-store">{STORE_ADDRESS}</div>
      </div>

      <div className="receipt-title">{values.title || t(type.titleKey)}</div>

      {values.note_number && (
        <div className="receipt-note">{values.note_number}</div>
      )}

      {type.layout === 'kassa' ? (
        <KassaBody values={values} t={t} />
      ) : type.layout === 'compra_ouro' ? (
        <>
          {values.cliente && (
            <table className="receipt-table">
              <tbody>
                <tr>
                  <td className="receipt-label">{t('rcpt_f_cliente')}</td>
                  <td className="receipt-val">{values.cliente}</td>
                </tr>
              </tbody>
            </table>
          )}
          <CompraOuroBody values={values} currency={currency} t={t} />
        </>
      ) : (
        <table className="receipt-table">
          <tbody>
            {rows.map((f) => (
              <tr key={f.key}>
                <td className="receipt-label">{t(f.labelKey)}</td>
                <td className="receipt-val">{renderValue(f, values[f.key], currency, values)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="receipt-sign">{t('rcpt_sign')}:____________________________</div>

      <div className="receipt-footer">
        <div>{t('rcpt_footer_1')}</div>
        <div>{t('rcpt_footer_2')}</div>
        <div className="receipt-timestamp">{timestamp}</div>
      </div>
    </div>
  );
}
